import { createFileRoute, notFound } from '@tanstack/react-router';
import { z } from 'zod';

import { ensureRecord } from '@/app/guards';
import { ConfigVersionPage, bankQuery, configVersionQuery, type ProductCode } from '@/features/banks-products';
import { NotFoundView } from '@/features/shell';

const PRODUCTS: readonly string[] = ['PF', 'CC', 'HF', 'SME'];

export const Route = createFileRoute('/_app/banks/$bankId/products/$productId/versions/$versionNo')({
  validateSearch: z.object({
    mode: z.enum(['view', 'compare']).optional().catch(undefined),
    base: z.coerce.number().int().positive().optional().catch(undefined),
  }),
  loader: async ({ context, params }) => {
    const versionNo = Number(params.versionNo);
    if (!PRODUCTS.includes(params.productId) || !Number.isInteger(versionNo)) throw notFound();
    const [bank, version] = await Promise.all([
      ensureRecord(context.queryClient.query({ ...bankQuery(params.bankId), staleTime: 'static' })),
      ensureRecord(context.queryClient.query({ ...configVersionQuery(params.bankId, params.productId as ProductCode, versionNo), staleTime: 'static' })),
    ]);
    return { crumb: `${bank.code} · ${params.productId} v${version.versionNo}` };
  },
  notFoundComponent: function VersionNotFound() {
    const { bankId, productId, versionNo } = Route.useParams();
    return <NotFoundView reference={`${bankId} · ${productId} v${versionNo}`} />;
  },
  component: function ConfigVersion() {
    const params = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    return (
      <ConfigVersionPage
        bankId={params.bankId}
        product={params.productId as ProductCode}
        versionNo={Number(params.versionNo)}
        mode={search.mode ?? 'view'}
        base={search.base}
        onSelectVersion={(n, replace) => void navigate({ params: { ...params, versionNo: String(n) }, search: (prev) => ({ ...prev, base: prev.mode === 'compare' ? prev.base : undefined }), replace: replace ?? false })}
        onCompareChange={({ mode, base }) => void navigate({ search: { mode: mode === 'compare' ? 'compare' : undefined, base: mode === 'compare' ? base : undefined }, replace: true })}
        onLeave={() => void navigate({ to: '/banks/$bankId', params: { bankId: params.bankId } })}
      />
    );
  },
});
